import React, {
  useContext,
  useState,
  useMemo,
  useCallback,
  useRef,
  useEffect,
} from "react";
import { useSelector } from "react-redux";
import { DF_OT0022Context } from "../context";
import { Row, Spinner } from "reactstrap";
import TableSimple from "../../../components/Dynamic/TableSimple";
import ModalIframePage from "../../../components/Common/ModalIframePage";
import BizinBackend from "../../../helpers/bizin_helper";
import useGetCsrfToken from "../../../Hooks/useGetCsrfToken";
import DF_OT0023 from "../DF_OT0023";
import DF_OT0024 from "../DF_OT0024";

const PROC_NAME = "FRONT_Workspace_DF_OT0022_Tabela_Grid_V16";

const diaParaIcone = (valor) => {
  if (valor === "X") {
    return {
      tipo: "icone",
      valor: "fas fa-check-circle text-primary",
      descricao: "",
    };
  }
  return { tipo: "texto", valor: "" };
};

const statusParaIcone = (valor) => ({
  tipo: "icone",
  valor:
    valor === "Ativo"
      ? "fas fa-check-circle text-success"
      : "fas fa-times-circle text-danger",
  descricao: valor,
});

function montarParametros(paramsOnFilter, user) {
  const p = paramsOnFilter || {};
  const userId = user?.id_usuario || user?.id || user?.ID || 7983;

  return {
    1: userId,
    2: "pt-BR",
    3: 1,
    4: 10000,
    5: p.Filtro_ID_Empresa_Origem || null,
    6: p.Filtro_ID_Empresa_Destino || null,
    7: p.Filtro_ID_Local_Entrega || null,
    8: p.Filtro_Status || null,
  };
}

export default function GridBody() {
  const {
    paramsOnFilter,
    triggerOnFilter,
    setTriggerOnFilter,
    inputGlobalSearchValue,
    setFilteredData,
    isLoading,
    setisLoading,
  } = useContext(DF_OT0022Context) || {};

  const csrfToken = useGetCsrfToken();
  const user = useSelector((state) => state.login.user);

  const [triggerType, setTriggerType] = useState(null);
  const [openModalVisualization, setOpenModalVisualization] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [selectedRowData, setSelectedRowData] = useState(null);

  const filteredDataRef = useRef([]);

  const handleCloseModalVisualization = useCallback(() => {
    setOpenModalVisualization(false);
    setTriggerType(null);
    setSelectedRowData(null);
    setTriggerOnFilter(true);
  }, [setTriggerOnFilter]);

  const handleModalIframeClosed = useCallback(() => {
    handleCloseModalVisualization();
  }, [handleCloseModalVisualization]);

  const handleFilteredDataChange = useCallback(
    (data) => {
      filteredDataRef.current = data || [];
      setFilteredData(data);
    },
    [setFilteredData],
  );

  const handleTriggers = useCallback(
    (trigger) => {
      const source = filteredDataRef.current?.length
        ? filteredDataRef.current
        : tableData;

      const rowContext = trigger?.rowData || trigger?.row || trigger?.data || trigger;
      let idReal = rowContext?.ID_Parametro_Parceiro ?? trigger?.ID_Parametro_Parceiro ?? null;

      if (!idReal) {
        try {
          const activeElement = document.activeElement;
          const clickTarget = window.event?.target || activeElement;
          if (clickTarget) {
            const trElement = clickTarget.closest("tr");
            if (trElement) {
              const rowIndex = trElement.sectionRowIndex;
              if (typeof rowIndex === "number" && source[rowIndex]) {
                idReal = source[rowIndex].ID_Parametro_Parceiro;
              }
            }
          }
        } catch (err) {
          console.warn("⚠️ Falha ao ler index físico no DOM:", err);
        }
      }

      let adjustedParams = trigger?.params ?? "";
      if (idReal && adjustedParams.includes("<#ID_AGENDAMENTO#>")) {
        adjustedParams = adjustedParams.replace("<#ID_AGENDAMENTO#>", idReal);
      }

      const rowData = idReal != null
        ? source.find((row) => String(row.ID_Parametro_Parceiro) === String(idReal))
        : null;

      setSelectedRowData(rowData);
      setTriggerType({
        ...trigger,
        params: adjustedParams,
      });
      setOpenModalVisualization(true);
    },
    [tableData],
  );

  const transformRow = useCallback(
    (row) => {
      let funcoesTratadas = row.Coluna_Funcoes;

      if (typeof row.Coluna_Funcoes === "string" && row.Coluna_Funcoes.trim() !== "") {
        try {
          funcoesTratadas = JSON.parse(row.Coluna_Funcoes);
        } catch (e) {
          console.error("Erro ao fazer parse da Coluna_Funcoes:", e);
        }
      }

      return {
        ...row,
        Coluna_Funcoes: funcoesTratadas,
        Quantidade_Anular: "",
        Status: statusParaIcone(row.Status),
        Segunda: diaParaIcone(row.Segunda),
        Terca: diaParaIcone(row.terca),
        Quarta: diaParaIcone(row.Quarta),
        Quinta: diaParaIcone(row.Quinta),
        Sexta: diaParaIcone(row.Sexta),
        Sabado: diaParaIcone(row.Sabado),
        Domingo: diaParaIcone(row.Domingo),
      };
    },
    [],
  );

  const buscarDados = useCallback(async () => {
    try {
      if (setisLoading) setisLoading(true);

      const procParams = montarParametros(paramsOnFilter, user);

      const data = await BizinBackend.executeProcedure(
        procParams,
        PROC_NAME,
        csrfToken,
        user?.access_token,
      );

      const d = data?.resultset_1 || data;
      setTableData(Array.isArray(d) ? d.map(transformRow) : []);
    } catch (error) {
      console.error("Erro ao buscar dados [DF_OT0022]:", error);
      setTableData([]);
    } finally {
      if (setisLoading) setisLoading(false);
      if (setTriggerOnFilter) setTriggerOnFilter(false);
    }
  }, [paramsOnFilter, csrfToken, user, transformRow, setTriggerOnFilter, setisLoading]);

  useEffect(() => {
    if (triggerOnFilter) {
      buscarDados();
    }
  }, [triggerOnFilter, buscarDados]);

  const columnsList = useMemo(
    () => [
      { field: "Status", header: "Status", width: "5%", hasImage: true },
      { field: "Nome_Empresa_Origem", header: "Origem" },
      { field: "Nome_Empresa_Destino", header: "Destino" },
      { field: "Local_Entrega", header: "Entrega" },
      { field: "Local_Desembarque", header: "Desembarque" },
      { field: "Limite_Financeiro_Diario", header: "Lim. Dia" },
      { field: "Limite_Financeiro_Semanal", header: "Lim. Semana" },
      { field: "Limite_Financeiro_Mensal", header: "Lim. Mês" },
      { field: "Tempo_Medio_Descarga", header: "Médio Descarga" },
      { field: "Exclusividade", header: "Exclusividade" },
      { field: "Segunda", header: "Seg", width: "3%", hasImage: true },
      { field: "Terca", header: "Ter", width: "3%", hasImage: true },
      { field: "Quarta", header: "Qua", width: "3%", hasImage: true },
      { field: "Quinta", header: "Qui", width: "3%", hasImage: true },
      { field: "Sexta", header: "Sex", width: "3%", hasImage: true },
      { field: "Sabado", header: "Sab", width: "3%", hasImage: true },
      { field: "Domingo", header: "Dom", width: "3%", hasImage: true },
      {
        field: "Coluna_Funcoes",
        header: "",
        width: "5%",
        hasImage: true,
        Export: false,
      },
    ],
    [],
  );

  const isDetalhesAgendamento = triggerType?.transaction_id?.includes("DF_OT0023");
  const isEditarAgendamento = triggerType?.transaction_id?.includes("DF_OT0024");

  return (
    <React.Fragment>
      <Row>
        <div 
          className="position-relative w-100 rounded"
          style={{ minHeight: "150px" }}
        >
          {isLoading && (
            <div className="position-absolute top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center bg-white bg-opacity-75 z-index-10 rounded">
              <Spinner className="text-primary" size="sm" />
            </div>
          )}

          <TableSimple
            dataKey={"ID_Parametro_Parceiro"}
            data={tableData || []}
            columnsList={columnsList}
            triggerOnFilter={false}
            setTriggerOnFilter={setTriggerOnFilter}
            inputSearchValue={inputGlobalSearchValue}
            enableDataSharing={true}
            onFilteredDataChange={handleFilteredDataChange}
            callbackTriggers={handleTriggers}
          />
        </div>

        {isDetalhesAgendamento && (
          <DF_OT0023
            isOpen={openModalVisualization}
            toggle={handleCloseModalVisualization}
            idParceiro={selectedRowData?.ID_Parametro_Parceiro || null}
          />
        )}

        {isEditarAgendamento && (
          <DF_OT0024
            isOpen={openModalVisualization}
            toggle={handleCloseModalVisualization}
            selectedRowData={selectedRowData}
          />
        )}

        {!isDetalhesAgendamento && !isEditarAgendamento && (
          <ModalIframePage
            openModal={openModalVisualization}
            onClosed={handleModalIframeClosed}  
            triggerType={triggerType}
          />
        )}
      </Row>
    </React.Fragment>
  );
}